import type { SearchFilters } from '@/entities/profile';
import { profileApi } from '@/features/profile-management';
import { getStorageUrl } from '@/shared/lib';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Separator } from '@/shared/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/ui/sheet';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  MapPin,
  Navigation,
  Search,
  Star,
  Users,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

// Countries available in the legacy system
const countries = [
  { value: 'FR', label: 'France' },
  { value: 'BE', label: 'Belgique' },
  { value: 'CH', label: 'Suisse' },
  { value: 'DE', label: 'Allemagne' },
  { value: 'NL', label: 'Pays-Bas' },
  { value: 'GB', label: 'Royaume-Uni' },
];

// French regions
const frenchRegions = [
  { value: 'ile-de-france', label: 'Île-de-France' },
  { value: 'auvergne-rhone-alpes', label: 'Auvergne-Rhône-Alpes' },
  { value: 'provence-alpes-cote-azur', label: "Provence-Alpes-Côte d'Azur" },
  { value: 'occitanie', label: 'Occitanie' },
  { value: 'nouvelle-aquitaine', label: 'Nouvelle-Aquitaine' },
  { value: 'hauts-de-france', label: 'Hauts-de-France' },
  { value: 'grand-est', label: 'Grand Est' },
  { value: 'pays-de-la-loire', label: 'Pays de la Loire' },
  { value: 'bretagne', label: 'Bretagne' },
  { value: 'normandie', label: 'Normandie' },
  { value: 'centre-val-de-loire', label: 'Centre-Val de Loire' },
  { value: 'bourgogne-franche-comte', label: 'Bourgogne-Franche-Comté' },
  { value: 'corse', label: 'Corse' },
];

// Nationalities
const nationalities = [
  { value: 'french', label: 'Française' },
  { value: 'belgian', label: 'Belge' },
  { value: 'moroccan', label: 'Marocaine' },
  { value: 'algerian', label: 'Algérienne' },
  { value: 'tunisian', label: 'Tunisienne' },
  { value: 'turkish', label: 'Turque' },
  { value: 'other', label: 'Autre' },
];

// Proximity search radius options (in km)
const proximityOptions = [5, 10, 20, 40, 80];

export default function ProfilesPage() {
  const { t } = useTranslate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<SearchFilters>({});
  const [tempFilters, setTempFilters] = useState<SearchFilters>({});
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['profiles', page, filters],
    queryFn: () => profileApi.getProfiles(filters, page, 12),
  });

  const handleFilterChange = (key: keyof SearchFilters, value: string | number | undefined) => {
    setTempFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    setIsSheetOpen(false);
  };

  const clearFilters = () => {
    setTempFilters({});
    setFilters({});
    setPage(1);
  };

  const handleProximitySearch = (radius: number) => {
    setFilters((prev) => ({ ...prev, radiantkm: radius }));
    setPage(1);
  };

  // Generate age options (18-99)
  const ageOptions = Array.from({ length: 82 }, (_, i) => i + 18);

  // Check if France is selected to show regions
  const showRegions = tempFilters.country === 'FR';

  // Active filter count
  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== '',
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {t('search.title', 'Découvrir')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('search.subtitle', 'Trouvez des profils qui correspondent à vos critères')}
          </p>
        </div>

        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('search.placeholder', 'Code postal ou pseudo...')}
              className="pl-10 w-[220px] h-11"
              value={tempFilters.wildcard || ''}
              onChange={(e) => handleFilterChange('wildcard', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>

          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="h-11 px-4 relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtres
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-primary text-xs text-primary-foreground flex items-center justify-center font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="overflow-y-auto w-full sm:max-w-md">
              <SheetHeader>
                <SheetTitle className="font-display text-xl">
                  {t('search.filters', 'Filtres de recherche')}
                </SheetTitle>
                <SheetDescription>
                  {t(
                    'search.filtersDescription',
                    'Affinez votre recherche pour trouver la bonne personne',
                  )}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-2 space-y-6 px-4 pb-6">
                {/* Age Range */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    {t('search.ageRange', "Tranche d'âge")}
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      value={String(tempFilters.ageFrom || '')}
                      onValueChange={(value) =>
                        handleFilterChange('ageFrom', Number.parseInt(value))
                      }
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder={t('search.from', 'De')} />
                      </SelectTrigger>
                      <SelectContent>
                        {ageOptions.map((age) => (
                          <SelectItem key={age} value={String(age)}>
                            {age} {t('search.years', 'ans')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={String(tempFilters.ageTo || '')}
                      onValueChange={(value) => handleFilterChange('ageTo', Number.parseInt(value))}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder={t('search.to', 'À')} />
                      </SelectTrigger>
                      <SelectContent>
                        {ageOptions.map((age) => (
                          <SelectItem key={age} value={String(age)}>
                            {age} {t('search.years', 'ans')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Country */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    {t('search.country', 'Pays de résidence')}
                  </Label>
                  <Select
                    value={tempFilters.country || ''}
                    onValueChange={(value) => {
                      handleFilterChange('country', value);
                      if (value !== 'FR') {
                        handleFilterChange('region', undefined);
                      }
                    }}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue
                        placeholder={t('search.selectCountry', 'Sélectionner un pays')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Region (only for France) */}
                {showRegions && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('search.region', 'Région')}</Label>
                    <Select
                      value={tempFilters.region || ''}
                      onValueChange={(value) => handleFilterChange('region', value)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue
                          placeholder={t('search.selectRegion', 'Sélectionner une région')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {frenchRegions.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Separator />

                {/* Nationality */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    {t('search.nationality', 'Nationalité')}
                  </Label>
                  <Select
                    value={String(tempFilters.nationality || '')}
                    onValueChange={(value) =>
                      handleFilterChange('nationality', Number.parseInt(value) || undefined)
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t('search.selectNationality', 'Toutes')} />
                    </SelectTrigger>
                    <SelectContent>
                      {nationalities.map((nat) => (
                        <SelectItem key={nat.value} value={nat.value}>
                          {nat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Origin Nationality */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">{t('search.origin', 'Origines')}</Label>
                  <Select
                    value={String(tempFilters.originNationality || '')}
                    onValueChange={(value) =>
                      handleFilterChange('originNationality', Number.parseInt(value) || undefined)
                    }
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t('search.selectOrigin', 'Toutes')} />
                    </SelectTrigger>
                    <SelectContent>
                      {nationalities.map((nat) => (
                        <SelectItem key={nat.value} value={nat.value}>
                          {nat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Marital Status */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">
                    {t('search.maritalStatus', 'Situation familiale')}
                  </Label>
                  <Select
                    value={tempFilters.maritalStatus || ''}
                    onValueChange={(value) => handleFilterChange('maritalStatus', value)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t('search.any', 'Toutes les situations')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">{t('search.single', 'Célibataire')}</SelectItem>
                      <SelectItem value="divorced">{t('search.divorced', 'Divorcé(e)')}</SelectItem>
                      <SelectItem value="widowed">{t('search.widowed', 'Veuf/Veuve')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-6">
                  <Button onClick={applyFilters} className="flex-1" size="lg">
                    {t('search.apply', 'Lancer la recherche')}
                  </Button>
                  <Button variant="outline" onClick={clearFilters} size="lg">
                    {t('search.clear', 'Effacer')}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Proximity Search */}
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <div className="h-8 w-8 rounded-lg bg-primary-surface flex items-center justify-center">
                <Navigation className="h-4 w-4 text-primary" />
              </div>
              {t('search.nearMe', 'Autour de moi')}
            </div>
            <div className="flex flex-wrap gap-2">
              {proximityOptions.map((km) => (
                <Button
                  key={km}
                  variant={filters.radiantkm === km ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleProximitySearch(km)}
                  className="h-9"
                >
                  {km} km
                </Button>
              ))}
              {filters.radiantkm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters((prev) => ({ ...prev, radiantkm: undefined }))}
                  className="h-9 text-muted-foreground"
                >
                  {t('search.clearProximity', 'Annuler')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      {data?.total !== undefined && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>
            <strong className="text-foreground">{data.total}</strong>{' '}
            {t('search.results', 'profil(s) trouvé(s)')}
          </span>
        </div>
      )}

      {/* Profiles Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(9)].map((_, i) => (
            <Card key={i} className="overflow-hidden h-full">
              <div className="flex gap-4 p-4 min-h-[200px]">
                <Skeleton className="h-28 w-28 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : data?.items && data.items.length > 0 ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((profile) => (
              <Link key={profile.nodeId} to={`/profile/${profile.nodeId}`} className="h-full block">
                <Card className="card-profile overflow-hidden cursor-pointer group h-full">
                  <CardContent className="p-4 min-h-[200px] h-full">
                    <div className="flex gap-4 h-full">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <Avatar className="h-28 w-28 rounded-xl ring-2 ring-border group-hover:ring-primary/30 transition-all duration-300">
                          <AvatarImage
                            src={getStorageUrl(profile.avatarPath)}
                            alt={profile.username}
                            className="object-cover"
                          />
                          <AvatarFallback className="text-2xl rounded-xl bg-primary-surface text-primary font-semibold">
                            {profile.username?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {/* Photo verified status */}
                        <div
                          className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5 ${
                            profile.isPhotoVerified
                              ? 'bg-success text-white'
                              : 'bg-destructive text-white'
                          }`}
                        >
                          {profile.isPhotoVerified ? (
                            <>
                              <CheckCircle2 className="h-3 w-3" />
                              Vérifié
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3" />
                              Non vérifié
                            </>
                          )}
                        </div>
                        {/* Online status */}
                        {profile.isOnline && (
                          <div className="absolute top-1 right-1 h-3.5 w-3.5 bg-success rounded-full border-2 border-card" />
                        )}
                        {/* Star rating */}
                        {Number(profile.note) > 0 && (
                          <div className="absolute top-1 left-1 flex bg-card/80 backdrop-blur-sm rounded px-1 py-0.5">
                            {Array.from({ length: Math.min(Number(profile.note), 5) }).map(
                              (_, i) => (
                                <Star key={i} className="h-3 w-3 text-accent fill-accent" />
                              ),
                            )}
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <h3 className="font-semibold truncate text-base">{profile.username}</h3>

                        <p className="text-sm font-medium text-primary">
                          {Number(profile.age) > 0 && `${profile.age} ans`}
                          {profile.maritalStatus && ` • ${profile.maritalStatus}`}
                        </p>

                        {/* Administrative state badge */}
                        {profile.administrativeState && profile.administrativeState > 0 && (
                          <Badge variant="destructive" className="text-[10px] h-5">
                            <AlertCircle className="h-3 w-3 mr-0.5" />
                            {profile.administrativeState === 1
                              ? 'En visite'
                              : profile.administrativeState === 2
                                ? 'Voyageur'
                                : profile.administrativeState}
                          </Badge>
                        )}

                        {profile.profession && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1.5 truncate">
                            <Briefcase className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70" />
                            {profile.profession}
                          </p>
                        )}

                        {(profile.city || profile.region) && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1.5 truncate">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70" />
                            {profile.city}
                            {profile.region && `, ${profile.region}`}
                          </p>
                        )}

                        {/* Nationality & Origin flags */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                          {Number(profile.nationality) > 0 && (
                            <span className="flex items-center gap-1" title="Nationalité">
                              <img
                                src={`/flags/${profile.nationality}.png`}
                                alt="Nationalité"
                                width={18}
                                height={12}
                                className="inline-block rounded-sm"
                              />
                            </span>
                          )}
                          {Number(profile.origin) > 0 && (
                            <span className="flex items-center gap-1" title="Origine">
                              <img
                                src={`/flags/${profile.origin}.png`}
                                alt="Origine"
                                width={18}
                                height={12}
                                className="inline-block rounded-sm"
                              />
                            </span>
                          )}
                        </div>

                        {profile.lastActivity && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 pt-0.5">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(profile.lastActivity), {
                              addSuffix: true,
                              locale: fr,
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-2 pt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-10 w-10"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-1 px-4">
              {Array.from({ length: Math.min(5, Math.ceil((data.total || 0) / 12)) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className="h-10 w-10"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              {Math.ceil((data.total || 0) / 12) > 5 && (
                <>
                  <span className="text-muted-foreground px-2">...</span>
                  <Button
                    variant={page === Math.ceil((data.total || 0) / 12) ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPage(Math.ceil((data.total || 0) / 12))}
                    className="h-10 w-10"
                  >
                    {Math.ceil((data.total || 0) / 12)}
                  </Button>
                </>
              )}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= Math.ceil((data.total || 0) / 12)}
              className="h-10 w-10"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center py-16">
          <div className="h-16 w-16 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-4">
            <Search className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-display text-xl font-semibold mb-2">
            {t('search.noResults', 'Aucun profil trouvé')}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
            {t(
              'search.noResultsDescription',
              'Essayez de modifier vos critères de recherche pour élargir les résultats',
            )}
          </p>
          <Button onClick={clearFilters}>{t('search.clearAll', 'Effacer les filtres')}</Button>
        </div>
      )}
    </div>
  );
}
